import React, { useState, useRef, useCallback } from 'react';
import classNames from 'classnames';
import {
  Button,
  makeStyles,
  tokens,
  shorthands,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import { LightbulbFilamentRegular } from '@fluentui/react-icons';
import { getFluctuationContentStyle } from '@bingads-webui-campaign-react/common-styles';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { IColumnData } from '../types';

export const useStyles = makeStyles({
  button: {
    color: tokens.colorNeutralForeground2BrandHover,
  },
  popover: {
    '& .bingads-fluctuation-wrapper': {
      width: '500px',
      '& .fluc-description': {
        fontSize: '12px',
      },
      '& .cause-container-wrapper': {
        paddingLeft: '31px',
        marginLeft: '-16px',
        paddingRight: '31px',
        marginRight: '-16px',
      },
    },
    '& .loading-dialog': {
      position: 'static',
      boxShadow: 'none',
      ...shorthands.borderStyle('none'),
    },
  },
});

const backupPalette = { themePrimary: tokens.colorPaletteBlueBorderActive };
const getStyles = ({ palette = backupPalette, iconFontFamily }) => ({ popover: { ...getFluctuationContentStyle({ palette, iconFontFamily }) } });

interface InsightCellProps {
  i18n: any;
  fluctuationTypeIds: Array<string>;
  renderFluctuationPopup: Function;
  entity: IColumnData;
  metric: { name: string; value: string; };
}

export function InsightCell({
  i18n, fluctuationTypeIds, renderFluctuationPopup, entity, metric,
}: InsightCellProps) {
  const classes = useStyles();

  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const styles = useDefaultStyles(getStyles);

  const onOpenChange = useCallback(((e, data) => {
    if (data.open) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }), []);

  return (
    <div className={classNames('insight-cell')}>
      <Popover trapFocus withArrow open={isOpen} onOpenChange={onOpenChange}>
        <PopoverTrigger disableButtonEnhancement>
          <Tooltip content={i18n.getString(_TL_('Click to view insight'))} relationship="label">
            <Button
              className={classNames('insight-button-trigger', classes.button)}
              appearance="subtle"
              icon={<LightbulbFilamentRegular />}
              size="medium"
              onClick={() => setIsOpen(true)}
              ref={buttonRef}
            />
          </Tooltip>
        </PopoverTrigger>

        <PopoverSurface>
          <div className={classNames(styles.popover, classes.popover)}>
            {renderFluctuationPopup({
              fluctuationTypeIds, isOpen, setIsOpen, entity, metric,
            })}
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
}
